import { Injectable } from '@angular/core';

import { asyncScheduler, Observable, Observer } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ResourceState, ObservableValue, ResourceStatus } from '../models/progress-model';
import { HttpHeaders } from '@angular/common/http';

export type ResourceCallBack = (rsp: Response, res?: ResourceState, blob?: Blob) => void;

/**
* Fetch resources from the web
*/
@Injectable({ providedIn: 'root' })
export class FetchService {
    constructor() {
    }

    /**
    * Fetch a resource from the web asynchroniously (when the returned observable is subscribed to)
    * @param resState Resource state containing url and current state of resource
    * @param abortSignal Abort controler to abort ongoing download process
    * @param onLoad Callback function called when resource has been completely downloaded
    */
    public fetchResource(resState: ResourceState, abortSignal: AbortSignal, onLoad?: ResourceCallBack, options: { method?: string, body?: any, headers?: HeadersInit } = {}): ObservableValue<ResourceState> {
        let obs = new ObservableValue<ResourceState>(observer => {
            let chunks = [];

            // Retun immediately if request was cancled while in queue
            if(resState.status == 'Cancled') return this.cancelRequest(resState, observer, obs);
            let bodyMerged = { signal: abortSignal, method: options.method, body: JSON.stringify(options.body), headers: options.headers };

            // Fetch resource from server
            fetch(resState.url, bodyMerged).then(async response => {
                // Update with first progress event
                let size = parseInt(response.headers.get("Content-Length"));
                this.updateRequest('Downloading', 30, size, resState, observer, obs);

                // Read response as a stream to get continious progress
                return this.getBodyStream(response, chunks, resState, observer, obs);

            }).then(([bytes, type, headers, status]) => {
                // Mark resource state downloaded and cached
                this.updateRequest('Cached', bytes, bytes, resState, observer, obs);
                if(!onLoad) {
                    observer.complete();
                }

                // Call back on load
                if(onLoad) try {
                    let blob = new Blob(chunks, { type: type });
                    onLoad(new Response(blob, { headers }), resState, blob);
                    observer.complete();

                } catch(err) {
                    console.error("Error in fetch callback " + err);
                    // This might mean that we coulden save a response or something else fatal, so report error
                    observer.error(err);
                    observer.complete();
                };

            }).catch(err => {
                console.log(`Resource download was cancled: '${resState.name}' from mote:${resState.moteKey}`);
                // Ignore download abort and register other resource fetch errors
                if(resState.status == 'Cancled') {
                    observer.next(resState);
                    observer.complete();
                }
                else {
                    // console.log("Fetch options (method/headers):", + options.method, options.headers );
                    this.registerFailed(err, resState, observer, obs);
                }
            });
        });
        // Init resource state
        obs.lastVal = resState;
        return obs;
    }

    /**
     * Simplified get fetch request returning promise for response value and progress observer
     */
    public get(url: string, groupId = "SimpleFetch", name: string = "SimpleFetch", hotObserver: boolean = true): [Promise<Response>, ObservableValue<ResourceState>, AbortController] {
        // Create fetch resources
        let obs: ObservableValue<ResourceState>;
        let abort = new AbortController();
        let resource = new ResourceState(url, name, groupId, '');

        // Create new Promise of response
        let promise = new Promise<Response>((resolve, reject) => {
            obs = this.fetchResource(resource, abort.signal, (rsp) => {
                resolve(rsp);
            });
        });
        // Start fetch if this is not a cold subscription
        if(hotObserver) obs.subscribe();
        return [promise, obs, abort];
    }

    private getBodyStream(rsp: Response, chunks: Uint8Array[], resState: ResourceState, observer: Observer<ResourceState>, val: ObservableValue<ResourceState>) {
        // Mark resource state with size from header
        let size = parseInt(rsp.headers.get("Content-Length"));
        let type = rsp.headers.get("Content-Type");
        let headers = rsp.headers;

        if(rsp.status != 200) {
            return new Promise((resolve, reject) => {
                if(type && type.startsWith("text/json")) {
                    rsp.json().then(errMsg => reject(errMsg)).catch(err => reject("Could not parse JSON rsp:" + err));
                }
                else {
                    //console.log("Error rsp:", rsp);
                    reject("Server rejected request, status: " + rsp.status);
                }
            });
        }

        const reader = rsp.body.getReader();
        return new Promise<[number, string, Headers]>(async (resolve, reject) => {
            let readItr = this.readStreamGenerator(reader, chunks);
            let fileStream = this.asyncToObservable(readItr);

            fileStream.pipe(throttleTime(500, asyncScheduler, { leading: true, trailing: true })).subscribe((ev) => {
                if(ev.done) return resolve([ev.bytes, type, headers]);
                // Send download update
                this.updateRequest('Downloading', ev.bytes, size, resState, observer, val);
            }, err => reject(err));
        });
    }

    private cancelRequest(state: ResourceState, observer: Observer<ResourceState>, val: ObservableValue<ResourceState>) {
        state.status = 'Cancled';
        state.percentage = 100;
        state.bytesTotal = 0;
        state.bytesTransfered = 0;

        val.lastVal = state;
        observer.next(state);
        observer.complete();
    }

    private registerFailed(err: any, state: ResourceState, observer: Observer<ResourceState>, val: ObservableValue<ResourceState>) {
        let size = Math.round(state.bytesTotal / 1024 / 102.4) / 10;
        let transfered = Math.round(state.bytesTransfered / 1024 / 102.4) / 10;

        console.warn(`Could not fetch resource, err: '${err && err.message ? err.message : err}', url: ${state.url} size: ${transfered} Mb, transfered: ${transfered} Mb`);

        state.status = 'Error';
        state.error = new DOMException("Could not fetch resource: " + state.url, (err && err.name) ? err.name : "No Name");
        state.percentage = 100;

        val.lastVal = state;
        observer.next(state);
        observer.complete();
    }

    private updateRequest(status: ResourceStatus, bytes: number, bytesTotal: number, state: ResourceState, observer: Observer<ResourceState>, val: ObservableValue<ResourceState>) {
        state.status = status;
        state.percentage = 100;
        state.bytesTotal = bytesTotal;
        state.bytesTransfered = bytes;
        state.percentage = (bytes / bytesTotal) * 100;

        val.lastVal = state;
        observer.next(state);
    }

    /**
    * Create an iteratable from a readable stream
    * @param reader Stream reader
    * @param chunks Array to store stream results
    */
    private async *readStreamGenerator(reader, chunks: Uint8Array[]): AsyncIterableIterator<{ done: boolean, chunk: Uint8Array, bytes: number }> {
        let res = { done: false, value: null };
        let bytes = 0;

        while(!res.done) {
            // Await result and yield next result
            res = await reader.read();
            bytes += res.value ? res.value.byteLength : 0;
            // Push Uint8Array data chunk to chunk array (exclude the last undefined)
            if(res.value) chunks.push(res.value);
            yield { chunk: res.value, done: res.done, bytes: bytes };
        }
    }

    /**
    * Create an observable from a async iterator
    * @param iterable
    */
    private asyncToObservable<T>(iterable: AsyncIterableIterator<T>): Observable<T> {
        return new Observable(observer => void (async () => {
            try {
                // Get all values until iteratable is empty
                for await(const item of iterable) {
                    if(observer.closed) return;
                    // Notify listners about this value
                    observer.next(item);
                }
                observer.complete();
            } catch(e) {
                observer.error(e);
            }
        })());
    }
}
